import { useEffect, useState, type JSX, type ReactNode } from 'react'
import { RecordDiffersNote, contractDates } from '../components/vendor/ContractSource'
import {
  ApiError,
  api,
  can,
  daysUntil,
  pctValue,
  refusalReason,
  shortDate,
  tierLabel,
  tierRuleName,
  type Assessment,
  type InviteResult,
  type Me,
  type Vendor,
  type VendorTierView,
  codeWords,
  readIf,
} from '../lib/api'
import { CoverageCell, DataTable, ErrorBox, type Column } from '../components/DataTable'
import { LifecycleControl } from '../components/LifecycleControl'
import { VendorMessages } from '../components/VendorMessages'
import { personLabel, usePeopleNames } from '../lib/people'
import { exitApi, type ExitView } from '../lib/exit'
import { VendorActivation } from '../components/VendorActivation'
import { VendorOnboarding } from '../components/VendorOnboarding'
import { ExitSummaryCard } from '../components/exit/ExitSummaryCard'
import { VendorContacts } from '../components/VendorContacts'
import { VendorContracts } from '../components/VendorContracts'
import { ContractChip } from '../components/vendor/ContractChip'
import { DocumentIntelligence } from '../components/vendor/DocumentIntelligence'
import { VendorDataProcessing } from '../components/VendorDataProcessing'
import { VendorExposure } from '../components/VendorExposure'
import { VendorFindings } from '../components/VendorFindings'
import { VendorPolicy } from '../components/VendorPolicy'
import { VendorProfileEditor } from '../components/VendorProfileForm'
import { VendorRiskTypes } from '../components/VendorRiskTypes'
import { VendorRiskSummary } from '../components/VendorRiskSummary'
import { VendorStatutory } from '../components/VendorStatutory'
import { VendorSubprocessors } from '../components/VendorSubprocessors'
import { Skeleton } from '../components/ui'
import { tierAskFooter, tierEndFooter, tierRejectFooter } from '../components/footers'
import { Dialog, Status, Toggletip } from '../components/kit'
import { useSave } from '../lib/save'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { ActivityPanel } from '../components/vendor/ActivityPanel'
import { findingsReading, liveExitOf, vendorName } from '../components/vendor/facts'
import { NowLine } from '../components/vendor/NowLine'
import { RequestDrawer } from '../components/vendor/RequestDrawer'
import { useFlag } from '../lib/flags'
import { closeRequestDrawer, openRequestDrawer, requestDrawer, requestPanelAsked } from '../lib/requestDrawer'
import { SentNote } from '../components/vendor/SentNote'
import { Chip, StatusChip } from '../components/StatusChip'
import { statusWord } from '../lib/status'
import { takeSent } from '../lib/sentNote'
import { Overview } from '../components/vendor/Overview'
import { Part, PartSwitch, RecordPage, VendorTabBar, followAddress, vendorTabList, writePlace, type MenuCount } from '../components/vendor/VendorTabs'
import { VendorBreachRiskInput, VendorIncidentBreachBox } from '../components/vendor/VendorIncidentBreach'
import { VendorHeader } from '../components/vendor/VendorHeader'
import {
  partAnchor,
  partOf,
  placeFor,
  placeFromHash,
  type Place,
  type SectionKey,
} from '../components/vendor/sections'
import { recordOrigin, type Origin } from '../lib/origin'
import { VendorHistory } from './Audit'
import '../styles/studio.css'
import '../styles/vendor.css'

function yesNo(v: boolean | undefined) {
  return v ? <strong>Yes</strong> : <span className="muted">No</span>
}

function pretty(v: string | undefined | null, fallback = 'not recorded') {
  if (!v) return <span className="muted">{fallback}</span>
  return <>{codeWords(v)}</>
}

function text(v: string | undefined | null, fallback = 'not recorded') {
  if (!v) return <span className="muted">{fallback}</span>
  return <>{v}</>
}

function dateOrNot(v: string | undefined | null, fallback = 'not recorded') {
  if (!v) return <span className="muted">{fallback}</span>
  return <span className="mono">{shortDate(v)}</span>
}

// The vendor record (release 3.3, the 7 Oct plan section 3.3): the breadcrumb,
// a header of one line (who it is and where it stands), one line of facts, one
// slim line for the lifecycle and what waits on the reader, then the record's
// tabs, Overview | Profile | Risk | Data | Contracts | Messages | Offboarding |
// History. components/vendor/sections.ts says why the side menu of sections
// became tabs, and how every old address still lands where it did;
// components/vendor/VendorTabs.tsx draws the row and the parts.
//
// The tabs keep the reading order the sections had. Risk sits next to the
// profile because the band is the answer to "how bad" and the rating is the
// answer to "bad at what"; findings follow as "what is anybody doing about it",
// because a band is a reading and a finding is an obligation with an owner and a
// date. Data sits beside them because it is where those answers come from, and
// exposure is a traversal over what data holds, turned into who has to be told
// and by when. Approval reads all of them, so it comes after, beside the
// contracts it checks for.
//
// History is the one tab with a capability of its own. audit.read is not in
// the read class every console role holds, so the activity panel and the full
// trail are drawn only for roles the route answers.

// The tab row's id, shared by the row and the part it opens (PageTabs). One
// vendor record is on screen at a time.
const TABS_ID = 'vd-tab-'

export function VendorDetail({ vendorId, initialTab }: { vendorId: string; initialTab?: string }) {
  // The address is read first: a reload, a link and Back all say which tab and
  // part (sections.ts); the route's own tab is the fallback.
  const [place, setPlaceState] = useState<Place>(() => placeFromHash(window.location.hash, vendorId, can) ?? placeFor(initialTab, can))
  // Bumped by a move that asks for the part to take focus, and by a move to
  // another tab, which brings the tab row back into view (below).
  const [focusAsked, setFocusAsked] = useState(0)
  const [tabMoved, setTabMoved] = useState(0)
  // Where this visit began (Back model B4, lib/origin.ts), read once. A link on
  // this page to another of its tabs adds an entry that does not know it; the
  // visit's origin is written into that entry, so the breadcrumb still says
  // "My work / ..." after it.
  const [origin] = useState<Origin | null>(() => recordOrigin())

  // A tab is a place a reader goes to: choosing one pushes a history entry, so
  // Back steps out of it (Back model B5), and the entry keeps the visit's
  // origin. A part is a view of the same tab: choosing one replaces the entry.
  // Neither raises hashchange, so the record stays mounted and an edit in
  // progress elsewhere on the page is not thrown away (writePlace). A link on
  // the page to another place (Why?, All details, a next step) asks for focus
  // on the part it opens, so a keyboard is not left on the body.
  const go = (next: Place, opts: { focus?: boolean; how?: 'push' | 'replace' } = {}) => {
    if (opts.focus) setFocusAsked((n) => n + 1)
    if (next.section !== place.section) setTabMoved((n) => n + 1)
    writePlace(vendorId, place, next, opts.how)
    setPlaceState(next)
  }
  const goFocus = (next: Place) => go(next, { focus: true })

  // The tabs and parts opened on this visit, which stay in the page, hidden,
  // while another is on screen (VendorTabs.tsx, Part).
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    // A tab is kept as tab:<key>, a part as its own key: some share a word.
    const keys = ['tab:' + place.section, partOf(place)].filter((k): k is string => Boolean(k))
    setOpened((was) => (keys.every((k) => was.has(k)) ? was : new Set([...was, ...keys])))
  }, [place])
  const seen = (s: SectionKey) => place.section === s || opened.has('tab:' + s)

  // Back, Forward and a link on this page to one of its own tabs: the tab and
  // part follow the address (followAddress), and an old address is written
  // again as the tab's own.
  useEffect(() => {
    const sync = () => {
      const at = followAddress(vendorId, origin, can)
      if (at) setPlaceState((was) => (was.section === at.section && partOf(was) === partOf(at) ? was : at))
    }
    sync()
    window.addEventListener('hashchange', sync)
    window.addEventListener('popstate', sync)
    return () => {
      window.removeEventListener('hashchange', sync)
      window.removeEventListener('popstate', sync)
    }
  }, [vendorId, origin])

  const [vendor, setVendor] = useState<Vendor | null>(null)
  const [assessments, setAssessments] = useState<Assessment[] | null>(null)
  const [exitView, setExitView] = useState<ExitView | null>(null)
  const [messageCount, setMessageCount] = useState<number | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [editing, setEditing] = useState(false)
  // What the last write did, in the sentence that write earned. It survives
  // until the next one, because a tier that moved is the thing somebody came
  // back to the screen to check.
  const [outcome, setOutcome] = useState('')
  // Bumped when the sub-processor register changes, so the data panel re-reads
  // the residency view that depends on it.
  const [registerVersion, setRegisterVersion] = useState(0)
  const names = usePeopleNames()
  // The confirmation of a questionnaire just sent from this vendor, shown once
  // (lib/sentNote.ts).
  const [sent, setSent] = useState<InviteResult | null>(() => takeSent(vendorId))
  // The Approve request drawer, opened from any of its doors (lib/requestDrawer.ts),
  // the address among them: My work's row links to ?panel=request.
  const requestOpen = useFlag(requestDrawer, vendorId)
  useEffect(() => {
    const asked = () => {
      if (requestPanelAsked(window.location.hash)) openRequestDrawer(vendorId)
    }
    asked()
    window.addEventListener('hashchange', asked)
    return () => window.removeEventListener('hashchange', asked)
  }, [vendorId])

  useEffect(() => {
    let live = true
    setVendor(null)
    setAssessments(null)
    setError(null)
    setEditing(false)
    setOutcome('')
    Promise.all([api.vendor(vendorId), readIf('assessment.read', () => api.assessments(vendorId), { assessments: [] })])
      .then(([v, a]) => {
        if (!live) return
        setVendor(v)
        setAssessments(a.assessments)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    // The exit and the messages are read on their own, so the record still
    // opens for somebody either read refuses; only the header label, the
    // lifecycle sub-line and a menu count go.
    exitApi
      .vendorExit(vendorId)
      .then((v) => {
        if (live) setExitView(v)
      })
      .catch(() => undefined)
    api
      .vendorMessages(vendorId)
      .then((r) => {
        if (live) setMessageCount((r.messages?.length ?? 0) + (r.inbound?.length ?? 0))
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [vendorId])

  // A move to another tab from below the fold brings the tab row back to the
  // top of the window (stuck under the top line), so the new tab is read from
  // its start; from above the fold the page stays where it is.
  useEffect(() => {
    if (!tabMoved) return
    const mark = document.querySelector('.vd-tabbar-mark')
    const top = (document.querySelector('.topline') as HTMLElement | null)?.offsetHeight ?? 0
    const y = mark?.getBoundingClientRect().top
    if (y !== undefined && y < top) window.scrollTo(0, Math.max(0, window.scrollY + y - top))
  }, [tabMoved])

  // A move that asked for focus puts it on the part now on screen, for a
  // keyboard and a screen reader user who pressed a link to it.
  useEffect(() => {
    if (!focusAsked) return
    const part = partOf(place)
    if (part) document.getElementById(partAnchor(part))?.focus({ preventScroll: true })
    // place is read as it is when focus was asked for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusAsked])

  // After a write that can move the header, the band or the exit: re-read the
  // record and the exit together, because the lifecycle and the exit label are
  // drawn from both.
  function refresh() {
    api.vendor(vendorId).then(setVendor).catch(() => undefined)
    exitApi
      .vendorExit(vendorId)
      .then(setExitView)
      .catch(() => undefined)
  }

  if (error != null) return <ErrorBox error={error} what="This vendor" />
  if (!vendor)
    return (
      <div className="vd-page" aria-busy="true">
        <Skeleton lines={2} height={22} width="40%" />
        <div className="vd-now vd-skeleton">
          <Skeleton lines={1} />
        </div>
      </div>
    )

  const due = daysUntil(vendor.nextDueOn)
  const liveExit = liveExitOf(exitView?.exits)
  const chain = exitView?.exitApproval
  const findings = findingsReading(vendor)
  const gaps = vendor.statutory?.gaps.length ?? 0
  // The segmented control above the part names it, so a part has no title.
  const titled = false

  const counts: Partial<Record<SectionKey, MenuCount>> = {}
  if (gaps > 0) counts.profile = { n: gaps, hot: true, label: gaps + (gaps === 1 ? ' identifier missing' : ' identifiers missing') }
  if (findings.standing > 0)
    counts.risk = {
      n: findings.standing,
      hot: findings.worst === null || findings.worst === 'CRITICAL' || findings.worst === 'HIGH',
      label: findings.standing + (findings.standing === 1 ? ' open finding' : ' open findings'),
    }
  if (messageCount) counts.messages = { n: messageCount, label: messageCount + (messageCount === 1 ? ' message' : ' messages') }
  if (liveExit?.status === 'PROPOSED') counts.exit = { n: '!', hot: true, label: 'Offboarding proposed' }
  else if (liveExit?.status === 'OPEN' && chain && chain.total > chain.signed) {
    const left = chain.total - chain.signed
    counts.exit = { n: left, label: left + (left === 1 ? ' sign-off to go' : ' sign-offs to go') }
  }

  const columns: Column<Assessment>[] = [
    {
      key: 'reference',
      header: 'Reference',
      sortValue: (a) => a.reference,
      render: (a) => (
        <>
          <a className="cell-primary mono" href={'#/assessment/' + encodeURIComponent(a.id)}>
            {a.reference}
          </a>
          <span className="cell-sub">
            {a.isDelta ? 'Delta: ' + (a.deltaSectionKeys.join(', ') || 'sections not named') : 'Full questionnaire'}
          </span>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (a) => a.status,
      render: (a) => <StatusChip domain="questionnaire" code={a.status} late={(daysUntil(a.dueOn) ?? 0) < 0} />,
    },
    {
      key: 'trigger',
      header: 'Raised by',
      sortValue: (a) => a.triggerReason,
      render: (a) => pretty(a.triggerReason),
    },
    {
      key: 'due',
      header: 'Due',
      sortValue: (a) => a.dueOn ?? null,
      render: (a) => (a.dueOn ? <span className="mono">{shortDate(a.dueOn)}</span> : <span className="muted">no date</span>),
    },
    {
      key: 'submitted',
      header: 'Submitted',
      sortValue: (a) => a.submittedAt ?? null,
      render: (a) =>
        a.submittedAt ? <span className="mono">{shortDate(a.submittedAt)}</span> : <span className="muted">outstanding</span>,
    },
    {
      key: 'respondent',
      header: 'Respondent',
      sortValue: (a) => a.respondentName ?? null,
      render: (a) =>
        a.respondentName ? (
          <>
            {a.respondentName}
            {a.respondentEmail && <span className="cell-sub mono">{a.respondentEmail}</span>}
          </>
        ) : (
          <span className="muted">not yet identified</span>
        ),
    },
    {
      key: 'entitlement',
      header: 'Entitlement at issue',
      sortValue: (a) => a.entitlementStamp,
      render: (a) => <span className="mono">{a.entitlementStamp || '-'}</span>,
    },
  ]

  const outcomeNote: ReactNode = outcome && (
    <div className="note" role="status" style={{ marginBottom: 12 }}>
      {outcome}
    </div>
  )

  return (
    <RecordPage part={partOf(place)} opened={opened}>
      <VendorHeader
        vendor={vendor}
        assessments={assessments ?? []}
        liveExit={liveExit}
        names={names}
        onGo={goFocus}
        onEdit={() => {
          setOutcome('')
          setEditing(true)
          go({ section: 'profile', part: null })
        }}
      />
      <ContractChip vendorId={vendorId} stamp={vendor} />
      {sent && <SentNote sent={sent} onClose={() => setSent(null)} />}
      <NowLine
        vendor={vendor}
        assessments={assessments ?? []}
        liveExit={liveExit}
        signOffs={chain?.total ?? null}
        exitStage={
          liveExit?.status === 'OPEN' && chain ? { signed: chain.signed, total: chain.total, job: chain.stages.find((st) => st.next)?.job } : null
        }
        names={names}
        onGo={goFocus}
        onChanged={refresh}
      />
      <RequestDrawer vendor={vendor} open={requestOpen} onClose={() => closeRequestDrawer(vendorId)} onDecided={refresh} names={names} />

      <VendorTabBar
        id={TABS_ID}
        name={vendorName(vendor)}
        tabs={vendorTabList(counts, can)}
        value={place.section}
        onPick={(key, how) => go({ section: key, part: null }, { how })}
      />

      <div className={'vd-grid' + (place.section === 'overview' && can('audit.read') ? ' vd-grid-activity' : '')}>
        <div className="vd-body" id={TABS_ID + 'panel'} role="tabpanel" aria-labelledby={TABS_ID + place.section}>
          <PartSwitch place={place} part={partOf(place)} onPick={(p) => go(p)} />
          {place.section === 'overview' && (
            <Overview vendor={vendor} assessments={assessments ?? []} onGo={goFocus} />
          )}

          {seen('profile') && (
            <>
              {place.section === 'profile' && outcomeNote}
              <Part id="profile" label="Profile" titled={titled}>
                {!editing && (
                  <div className="profile-bar">
                    <button
                      type="button"
                      onClick={() => {
                        setOutcome('')
                        setEditing(true)
                      }}
                      disabled={!can('vendor.edit')}
                      title={can('vendor.edit') ? undefined : refusalReason('vendor.edit')}
                    >
                      Edit this record
                    </button>
                    {!can('vendor.edit') && <ReasonTip label="Why this record is read only" text={refusalReason('vendor.edit')} />}
                  </div>
                )}

                {editing && (
                  <VendorProfileEditor
                    vendor={vendor}
                    onCancel={() => setEditing(false)}
                    onSaved={(fresh, said) => {
                      setVendor(fresh)
                      setOutcome(said)
                      setEditing(false)
                    }}
                  />
                )}

                {!editing && (
                  <div className="grid-2">
                    <div className="vd-profile-main">
                      <section className="panel">
                        <h2>Basic information</h2>
                        <dl className="kv">
                          <dt>Legal name</dt>
                          <dd>{vendor.legalName}</dd>
                          <dt>Vendor code</dt>
                          <dd className="mono">{vendor.vendorCode}</dd>
                          <dt>Vendor type</dt>
                          <dd>{pretty(vendor.vendorType)}</dd>
                          <dt>Vendor category</dt>
                          <dd>{text(vendor.vendorCategory)}</dd>
                          <dt>Business function</dt>
                          <dd>{text(vendor.businessFunction)}</dd>
                          <dt>Relationship since</dt>
                          <dd>{dateOrNot(vendor.relationshipStartDate)}</dd>
                          <dt>Contract runs</dt>
                          <dd>
                            {contractDates(vendor).start || contractDates(vendor).end ? (
                              <>
                                {dateOrNot(contractDates(vendor).start, 'no start date')} to{' '}
                                {dateOrNot(contractDates(vendor).end, 'no end date')}
                                <RecordDiffersNote vendor={vendor} />
                              </>
                            ) : (
                              <span className="muted">no contract dates</span>
                            )}
                          </dd>
                          <dt>Lifecycle</dt>
                          <dd>
                            <LifecycleControl
                              vendorId={vendorId}
                              except={['offboard', 'offboarded']}
                              questionnaireSent={(assessments ?? []).length > 0}
                              onChanged={refresh}
                              onOpenActivation={() => go({ section: 'contracts', part: 'approval' }, { focus: true })}
                              onOpenExit={() => go({ section: 'exit', part: null }, { focus: true })}
                            />
                          </dd>
                          <dt>Record origin</dt>
                          <dd>{pretty(vendor.origin)}</dd>
                        </dl>
                      </section>

                      <section className="panel">
                        <h2>Identity</h2>
                        <dl className="kv">
                          <dt>Legal form</dt>
                          <dd>{pretty(vendor.constitution)}</dd>
                          <dt>Residency</dt>
                          <dd>{pretty(vendor.residency)}</dd>
                          <dt>PAN</dt>
                          <dd className="mono">{vendor.pan || <span className="muted">not on file</span>}</dd>
                          <dt>MSME</dt>
                          <dd>
                            {vendor.msmeCategory ? (
                              pretty(vendor.msmeCategory)
                            ) : (
                              <span className="vd-inline">
                                <span className="muted">not declared</span>
                                <Toggletip label="About MSME">Not declared, so MSME payment terms do not apply.</Toggletip>
                              </span>
                            )}
                          </dd>
                        </dl>
                      </section>

                      <section className="panel">
                        <h2>Who answers for this vendor</h2>
                        <dl className="kv">
                          <dt>Internal owner</dt>
                          <dd>{personLabel(names, vendor.ownerUserId) || <span className="muted">unassigned</span>}</dd>
                          <dt>Business owner</dt>
                          <dd>{personLabel(names, vendor.businessOwnerUserId) || <span className="muted">unassigned</span>}</dd>
                          <dt>Procurement owner</dt>
                          <dd>{personLabel(names, vendor.procurementOwnerUserId) || <span className="muted">unassigned</span>}</dd>
                          <dt>TPRM owner</dt>
                          <dd>{personLabel(names, vendor.tprmOwnerUserId) || <span className="muted">unassigned</span>}</dd>
                        </dl>
                      </section>

                      <section className="panel">
                        <h2>Location</h2>
                        <dl className="kv">
                          <dt>Registered</dt>
                          <dd>
                            {[vendor.stateCode, vendor.country].filter(Boolean).join(', ') || (
                              <span className="muted">not recorded</span>
                            )}
                          </dd>
                          <dt>Processing location</dt>
                          <dd>{text(vendor.processingLocation)}</dd>
                          <dt>Data centre location</dt>
                          <dd>{text(vendor.dataCentreLocation)}</dd>
                        </dl>
                      </section>

                      <section className="panel">
                        <h2>Services</h2>
                        <dl className="kv">
                          <dt>Service provided</dt>
                          <dd>{text(vendor.serviceDescription)}</dd>
                          <dt>Business process</dt>
                          <dd>{text(vendor.businessProcess)}</dd>
                          <dt>Criticality</dt>
                          <dd>{pretty(vendor.serviceCriticality)}</dd>
                          <dt>Dependency</dt>
                          <dd>{pretty(vendor.dependencyLevel)}</dd>
                        </dl>
                      </section>
                    </div>

                    <div>
                      <TierPanel
                        vendor={vendor}
                        names={names}
                        onClassified={(fresh, said) => {
                          setVendor(fresh)
                          setOutcome(said)
                        }}
                      />

                      <section className="panel">
                        <h2>Risk profile</h2>
                        <dl className="kv" style={{ gridTemplateColumns: '150px minmax(0, 1fr)' }}>
                          <dt>Data categories</dt>
                          <dd>
                            <div className="chiprow" style={{ alignItems: 'center' }}>
                              {vendor.dataCategories.length > 0 ? (
                                vendor.dataCategories.map((c) => (
                                  <span key={c} className="chip">
                                    {codeWords(c)}
                                  </span>
                                ))
                              ) : (
                                <span className="muted">none declared</span>
                              )}
                              {vendor.dataProcessing && (
                                <Toggletip hover label="Where the data categories come from">
                                  Read from the data elements recorded under Data.
                                </Toggletip>
                              )}
                            </div>
                          </dd>
                          <dt>Volume band</dt>
                          <dd>{pretty(vendor.volumeBand)}</dd>
                          <dt>System access</dt>
                          <dd>{pretty(vendor.systemAccess)}</dd>
                          <dt>Critical business service</dt>
                          <dd>{yesNo(vendor.criticalBusinessService)}</dd>
                          <dt>Production access</dt>
                          <dd>{yesNo(vendor.productionAccess)}</dd>
                          <dt>Customer facing</dt>
                          <dd>{yesNo(vendor.customerFacing)}</dd>
                          <dt>Continuity dependency</dt>
                          <dd>{pretty(vendor.continuityDependency)}</dd>
                          <dt>Regulatory impact</dt>
                          <dd>{pretty(vendor.regulatoryImpact, 'not stated')}</dd>
                          <dt>Regulated process</dt>
                          <dd>
                            <span className="vd-inline">
                              {yesNo(vendor.regulatedProcess)}
                              {vendor.regulatoryImpact && (
                                <Toggletip hover label="Where regulated process comes from">
                                  Set by the regulatory impact above: high and medium are regulated, low is not.
                                </Toggletip>
                              )}
                            </span>
                          </dd>
                          <dt>Material outsourcing</dt>
                          <dd>{yesNo(vendor.materialOutsourcing)}</dd>
                          <dt>Cross border</dt>
                          <dd>{yesNo(vendor.crossBorder)}</dd>
                          <dt>Subcontractors</dt>
                          <dd>{yesNo(vendor.subcontractorsDeclared)}</dd>
                          <dt>Sub-processors named</dt>
                          <dd>
                            {vendor.subprocessorCount === undefined ? (
                              <span className="muted">not read</span>
                            ) : (
                              <span className="vd-inline">
                                <span className="mono">{vendor.subprocessorCount}</span>
                                <Toggletip hover label="How sub-processors are counted">
                                  Parties named under Data. Withdrawn ones are not counted.
                                </Toggletip>
                              </span>
                            )}
                          </dd>
                          {/* Nothing in the console can set these two, so a "No"
                              beside every vendor was a claim nobody had made
                              (contradiction X5). Shown only where an import or
                              a data load recorded them as true. */}
                          {vendor.concentrationFlag === true && (
                            <>
                              <dt>Concentration</dt>
                              <dd>{yesNo(true)}</dd>
                            </>
                          )}
                          {vendor.singlePointFailure === true && (
                            <>
                              <dt>Single point of failure</dt>
                              <dd>{yesNo(true)}</dd>
                            </>
                          )}
                        </dl>
                      </section>

                      <section className="panel">
                        <h2>Current position</h2>
                        <dl className="kv">
                          <dt>Coverage</dt>
                          <dd>
                            <span style={{ display: 'inline-flex' }}>
                              <CoverageCell value={pctValue(vendor.coveragePct)} />
                            </span>
                          </dd>
                          <dt>Next due</dt>
                          <dd>
                            {vendor.nextDueOn ? (
                              <>
                                <span className="mono">{shortDate(vendor.nextDueOn)}</span>
                                {due !== null && due < 0 && (
                                  <span className="cell-sub" style={{ color: 'var(--accent)' }}>
                                    {Math.abs(due)} days overdue
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="muted">no reassessment cadence set</span>
                            )}
                          </dd>
                        </dl>
                      </section>
                    </div>
                  </div>
                )}
              </Part>
              <Part id="contacts" label="Contacts" titled={titled}>
                <VendorContacts vendorId={vendorId} />
              </Part>
              <Part id="statutory" label="Statutory and banking" titled={titled}>
                <VendorStatutory
                  vendor={vendor}
                  onSaved={(fresh, said) => {
                    setVendor(fresh)
                    setOutcome(said)
                  }}
                />
              </Part>
            </>
          )}

          {seen('risk') && (
            <>
              <Part id="risk" label="Risk" titled={titled}>
                <VendorRiskSummary vendor={vendor} assessments={assessments ?? []} />
                <VendorBreachRiskInput vendorId={vendorId} />
                <VendorIncidentBreachBox vendorId={vendorId} />
                <details>
                  <summary style={{ cursor: 'pointer', marginBottom: 8 }}>Coverage by risk type</summary>
                  <VendorRiskTypes vendorId={vendorId} />
                </details>
              </Part>
              <Part id="assessments" label="Assessments" titled={titled}>
                {/* The questionnaires raised against the vendor, each with its
                    state: what the rating is read from, so beside it (release
                    3.3, V1; on the Profile it made the Details part two
                    windows tall). */}
                <section className="panel panel-flush">
                  <div className="panel-head">
                    <h2 style={{ margin: 0 }}>Assessments</h2>
                    <p className="summary-line" style={{ margin: 0 }}>
                      <strong>{assessments?.length ?? 0}</strong> on record
                    </p>
                  </div>
                  <DataTable
                    caption={'Assessments raised against ' + (vendor.displayName || vendor.legalName) + ', newest first.'}
                    columns={columns}
                    rows={assessments ?? []}
                    rowKey={(a) => a.id}
                    defaultSort={{ key: 'due', dir: 'desc' }}
                    empty={{
                      title: 'No assessment raised yet',
                      detail: 'Raise one through a campaign or a trigger event.',
                    }}
                  />
                </section>
              </Part>
              <Part id="findings" label="Findings" titled={titled}>
                <VendorFindings vendorId={vendorId} />
              </Part>
              <Part id="reassessment" label="Reassessment" titled={titled}>
                <VendorPolicy vendorId={vendorId} />
              </Part>
            </>
          )}

          {seen('data') && (
            <>
              <Part id="data" label="Data" titled={titled}>
                <VendorDataProcessing
                  vendorId={vendorId}
                  refreshKey={registerVersion}
                  onSaved={() => {
                    // The record is a tier input, so the header band and the
                    // tier on the profile can both be out of date the moment
                    // it is saved. Re-reading is cheaper than telling somebody
                    // to reload.
                    refresh()
                  }}
                />
                <VendorSubprocessors
                  vendorId={vendorId}
                  onChanged={() => {
                    refresh()
                    // The residency panel above reads the sub-processor
                    // register, so naming a party abroad changes what it says.
                    setRegisterVersion((n) => n + 1)
                  }}
                />
              </Part>
              <Part id="exposure" label="Exposure" titled={titled}>
                <VendorExposure vendorId={vendorId} />
              </Part>
            </>
          )}

          {seen('contracts') && (
            <>
              {/* Release 3.3, documents lane: personal data values found inside
                  the files, and what the contract says the vendor will handle,
                  in two boxes that never mix. */}
              <Part id="documents" label="What the documents say" titled={titled}>
                <DocumentIntelligence vendorId={vendorId} onRecordChanged={refresh} />
              </Part>
              <Part id="contracts" label="Contracts" titled={titled}>
                <VendorContracts vendorId={vendorId} contractDates={{ start: vendor.contractStartDate, end: vendor.contractEndDate }} />
              </Part>
              {/* Before the gate, because it is the earlier question. The gate
                  says whether the evidence stands; the steps say whether the
                  process was followed, and a vendor can fail either. Two
                  parts since release 3.3 (V1): together they were two windows
                  and a half. */}
              <Part id="onboarding" label="Onboarding" titled={titled}>
                <VendorOnboarding vendorId={vendorId} />
              </Part>
              <Part id="approval" label="Approval" titled={titled}>
                <VendorActivation
                  vendorId={vendorId}
                  vendorName={vendor.displayName || vendor.legalName}
                  onSigned={() => {
                    // A signature does not change the vendor row, and the
                    // lifecycle control reads the gate when it mounts.
                    // Re-reading keeps the header band and the findings count
                    // honest if the signature was the last thing done.
                    refresh()
                  }}
                />
              </Part>
            </>
          )}

          {seen('messages') && (
            <Part id="messages" label="Messages" titled={false}>
              <VendorMessages vendorId={vendorId} />
            </Part>
          )}

          {seen('exit') && (
            <Part id="offboarding" label="Offboarding" titled={false}>
              {/* Where the exit is and the way into its workspace, which has
                  the page's full width at #/exits/<vendorId>. */}
              <ExitSummaryCard vendorId={vendorId} />
            </Part>
          )}

          {seen('history') && can('audit.read') && (
            <Part id="history" label="History" titled={false}>
              <VendorHistory vendorId={vendorId} />
            </Part>
          )}
        </div>

        {place.section === 'overview' && can('audit.read') && (
          <ActivityPanel vendorId={vendorId} names={names} onOpenHistory={() => goFocus({ section: 'history', part: null })} />
        )}
      </div>
    </RecordPage>
  )
}

// Who decided a tier and when, as one sentence for the (i) beside the rule.
// The panel names the rule; the rest is supporting detail, and the product
// officer's review of 17 September asked for detail of that kind to sit behind
// a hover rather than on the screen.
function decidedSentence(d: { ruleKey?: string; decidedAt?: string; decidedBy?: string }): string {
  const parts = [d.ruleKey ? 'Rule ' + d.ruleKey : 'Rule not recorded']
  if (d.decidedAt) parts.push('decided on ' + shortDate(d.decidedAt))
  parts.push(d.decidedBy ? 'by ' + d.decidedBy : 'by the rules, with nobody editing')
  return parts.join(', ') + '.'
}

// The four tiers in the order a person reads them, most exposed first. The
// stored ordinal runs the other way, which is why the list is not 1 to 4.
const TIER_CHOICES = [4, 3, 2, 1]

function asApiErr(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

function isoTomorrow(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

// The tier, as a label and never as the stored number. The ordinal runs the
// other way round from the name a person has learned, so printing it would tell
// the reader the opposite of the truth: see docs/DECISIONS.md.
//
// The history is fetched rather than inferred. "Why is this vendor Tier 1" is a
// question asked months later by somebody who was not in the room, and the
// answer is the row that was written on the day, not a re-derivation from the
// facts as they stand now.
//
// A tier can also be set by hand (the product officer's T1): one person asks,
// with a reason and an optional end date, and a second person who may decide a
// risk position approves it. While it stands the panel says who set it and who
// approved it, and shows what the rules say beside it when the two differ.
function TierPanel({
  vendor,
  names,
  onClassified,
}: {
  vendor: Vendor
  names: Record<string, string>
  onClassified: (v: Vendor, outcome: string) => void
}): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  const [view, setView] = useState<VendorTierView | null>(null)
  const [history, setHistory] = useState(false)
  const [me, setMe] = useState<Me | null>(null)
  const [reload, setReload] = useState(0)

  // The request form and the two decisions that take a sentence.
  const [asking, setAsking] = useState(false)
  const [askTier, setAskTier] = useState('')
  const [askReason, setAskReason] = useState('')
  const [askEnds, setAskEnds] = useState('')
  const [tried, setTried] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [ending, setEnding] = useState(false)
  const tierSave = useSave(asking ? 'ask' : rejecting ? 'reject' : ending ? 'end' : null, 'the tier change')
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<ApiError | null>(null)

  useEffect(() => {
    let live = true
    setView(null)
    api
      .vendorTier(vendor.id)
      .then((v) => {
        if (live) setView(v)
      })
      // The panel already has the standing tier from the record read, so a
      // history that cannot be fetched costs the list and nothing else.
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [vendor.id, vendor.tier?.decidedAt, reload])

  useEffect(() => {
    let live = true
    api
      .me()
      .then((m) => {
        if (live) setMe(m)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  const decision = vendor.tier
  const label = decision?.label || tierLabel(vendor.inherentTier)
  const past = (view?.history ?? []).filter((h) => h.decidedAt !== decision?.decidedAt)
  const standing = view?.override
  const pending = view?.pending
  const rules = view?.rules
  const rulesDiffer = standing != null && rules != null && rules.tier !== standing.tier
  const who = (s: string | undefined) => personLabel(names, s) || s || 'somebody'
  const mine = pending != null && me != null && me.subject === pending.requestedBy

  // After any act, the record and the panel are read again, so the tier the
  // header, the register and this panel show is the one the API now holds.
  function refresh(said: string) {
    return api.vendor(vendor.id).then((fresh) => {
      setReload((n) => n + 1)
      onClassified(fresh, said)
    })
  }

  function reclassify() {
    setBusy(true)
    setError(null)
    api
      .classifyVendor(vendor.id)
      .then((result) =>
        api.vendor(vendor.id).then((fresh) => {
          const now = fresh.tier?.label || tierLabel(fresh.inherentTier)
          let said = result.changed ? 'Tier moved from ' + (result.previousTierLabel || 'no tier') + ' to ' + now + '.' : 'Tier unchanged at ' + now + '.'
          if (result.setByHand) {
            said = 'The tier set by hand stands at ' + now + '.' + (result.rules ? ' The rules say ' + result.rules.label + '.' : '')
          }
          setReload((n) => n + 1)
          onClassified(fresh, said)
        }),
      )
      .catch((err) => setError(asApiErr(err)))
      .finally(() => setBusy(false))
  }

  function openAsk() {
    setAskTier('')
    setAskReason('')
    setAskEnds('')
    setTried(false)
    setFormError(null)
    setAsking(true)
  }



  function decide(verb: 'approve' | 'reject' | 'withdraw', words: string, withNote?: string) {
    if (!pending) return
    setBusy(true)
    setError(null)
    setFormError(null)
    api
      .decideTierOverride(vendor.id, pending.id, verb, withNote)
      .then(() => refresh(words))
      .then(() => setRejecting(false))
      .catch((err) => (verb === 'reject' ? setFormError(asApiErr(err)) : setError(asApiErr(err))))
      .finally(() => setBusy(false))
  }



  const canAsk = can('risk.accept')
  const canDecide = can('risk.approve')

  return (
    <section className="panel" aria-labelledby="tier-heading">
      <h2 id="tier-heading">Tier classification</h2>

      {label ? <p className="tier-label">{label}</p> : <p className="tier-label muted">Not classified</p>}

      {standing ? (
        <>
          <p className="tier-by" style={{ margin: 0 }}>
            <Status tone="info">Set by hand</Status> by {who(standing.requestedBy)}, approved by {who(standing.decidedBy)}{' '}
            <Toggletip hover label="Why this tier was set by hand">
              {'Reason: ' + standing.reason + (standing.expiresOn ? ' Ends on ' + shortDate(standing.expiresOn) + '.' : ' No end date.')}
            </Toggletip>
          </p>
          {rulesDiffer && (
            <p className="muted" style={{ margin: '6px 0 0' }}>
              The rules say {rules.label}
            </p>
          )}
        </>
      ) : decision?.recorded ? (
        // In the flow of the sentence rather than beside it, so a rule name
        // that wraps keeps its (i) at the end of the words.
        <p style={{ margin: 0 }}>
          {tierRuleName(decision.why, decision.ruleKey) || 'Rule not recorded'}{' '}
          <Toggletip hover label="How this tier was decided">
            {decidedSentence(decision)}
          </Toggletip>
        </p>
      ) : null}

      {pending && (
        <div className="tier-pending" role="status">
          <p style={{ margin: 0 }}>
            <Chip s={statusWord('approval', 'PENDING')} /> Change to <strong>{pending.tierLabel}</strong>, asked by{' '}
            {who(pending.requestedBy)}{' '}
            <Toggletip hover label="Why the change was asked for">
              {'Reason: ' + pending.reason + (pending.expiresOn ? ' Ends on ' + shortDate(pending.expiresOn) + '.' : '')}
            </Toggletip>
          </p>
          <div className="exc-actions">
            {canDecide && !mine && (
              <>
                <button
                  type="button"
                  onClick={() => decide('approve', 'Tier changed to ' + pending.tierLabel + '.')}
                  disabled={busy}
                  data-action="tprm.vendor,vendor-record.a33"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNote('')
                    setFormError(null)
                    setTried(false)
                    setRejecting(true)
                  }}
                  disabled={busy}
                >
                  Reject
                </button>
              </>
            )}
            {mine && (
              <>
                <button
                  type="button"
                  onClick={() => decide('withdraw', 'Tier change withdrawn.')}
                  disabled={busy}
                  data-action="tprm.vendor,vendor-record.a35"
                >
                  Withdraw
                </button>
                <span className="self-note">A second person approves it</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* The earlier tiers are history, so they open in a side panel rather
          than sitting under the one that stands. */}
      {past.length > 0 && (
        <p style={{ margin: '8px 0 0' }}>
          <button type="button" className="linkish" onClick={() => setHistory(true)}>
            Earlier tiers ({past.length})
          </button>
        </p>
      )}
      <Dialog shape="drawer" kind="record" open={history} onClose={() => setHistory(false)} title="Earlier tiers">
        <ul className="tier-legend">
          {past.map((h) => (
            <li key={(h.decidedAt ?? '') + (h.ruleKey ?? '') + (h.source ?? '')}>
              <strong>{h.label}</strong>
              <span>
                {h.source === 'MANUAL' ? 'set by hand' : h.ruleKey || 'rule not recorded'}
                {h.decidedAt ? ', ' + shortDate(h.decidedAt) : ''}
                {h.decidedBy ? ', ' + who(h.decidedBy) : ''}
              </span>
            </li>
          ))}
        </ul>
      </Dialog>

      {error && (
        <div className="error-box" role="alert">
          <h3>That did not go through ({error.code})</h3>
          <p>{error.message}</p>
          {error.remedy && <p className="muted">{error.remedy}</p>}
        </div>
      )}

      <div className="exc-actions">
        <button
          type="button"
          onClick={reclassify}
          disabled={busy || !can('vendor.edit')}
          title={can('vendor.edit') ? undefined : refusalReason('vendor.edit')}
          data-action="tprm.vendor,vendor-record.a31"
        >
          {busy ? 'Working' : 'Reclassify now'}
        </button>
        <button
          type="button"
          onClick={openAsk}
          disabled={busy || !canAsk || pending != null}
          title={!canAsk ? refusalReason('risk.accept') : pending ? 'A change is already waiting for approval' : undefined}
        >
          Change tier
        </button>
        {standing && (
          <button
            type="button"
            onClick={() => {
              setNote('')
              setFormError(null)
              setEnding(true)
            }}
            disabled={busy || !canDecide}
            title={canDecide ? undefined : refusalReason('risk.approve')}
          >
            End manual tier
          </button>
        )}
        {!can('vendor.edit') && !canAsk && (
          <ReasonTip label="Why changing the tier is not open to you" text={refusalReason('risk.accept')} />
        )}
      </div>

      <Dialog
        shape="modal"
        kind="form"
        open={asking}
        onClose={() => setAsking(false)}
        title="Change tier"
        {...tierAskFooter(
          { vendorId: vendor.id, tier: askTier, reason: askReason, ends: askEnds, tried, busy: tierSave.busy },
          (work) => {
            setFormError(null)
            tierSave.send(work, {
              done: () => {
                void refresh('Tier change to ' + tierLabel(Number(askTier)) + ' sent for approval.')
                setAsking(false)
              },
              failed: (err) => setFormError(asApiErr(err)),
            })
          },
          () => setTried(true),
        )}
      >
        {formError && <ErrorBox error={formError} what="The tier change" verb="sent" />}
        <div className="field">
          <label htmlFor="tier-ask-tier">New tier</label>
          <select
            id="tier-ask-tier"
            value={askTier}
            aria-invalid={tried && askTier === ''}
            onChange={(e) => setAskTier(e.target.value)}
          >
            <option value="">Choose a tier</option>
            {TIER_CHOICES.filter((t) => t !== vendor.inherentTier).map((t) => (
              <option key={t} value={String(t)}>
                {tierLabel(t)}
              </option>
            ))}
          </select>
          {tried && askTier === '' && <p className="field-error">Choose the tier</p>}
        </div>
        <div className="field">
          <label htmlFor="tier-ask-reason">Reason</label>
          <textarea
            id="tier-ask-reason"
            rows={3}
            maxLength={1000}
            value={askReason}
            placeholder="Why the rules have this vendor wrong"
            aria-invalid={tried && askReason.trim() === ''}
            onChange={(e) => setAskReason(e.target.value)}
          />
          {tried && askReason.trim() === '' && <p className="field-error">Say why, it goes on the record</p>}
        </div>
        <div className="field">
          <label htmlFor="tier-ask-ends">Ends on (optional)</label>
          <input id="tier-ask-ends" type="date" min={isoTomorrow()} value={askEnds} onChange={(e) => setAskEnds(e.target.value)} />
        </div>
        <p className="muted">A second person approves it before the tier changes.</p>
      </Dialog>

      <Dialog
        shape="modal"
        kind="form"
        open={rejecting}
        onClose={() => setRejecting(false)}
        title="Reject the tier change"
        {...tierRejectFooter(
          { vendorId: vendor.id, requestId: pending?.id ?? '', note, tried, busy: tierSave.busy },
          (work) => {
            setFormError(null)
            tierSave.send(work, {
              done: () => {
                void refresh('Tier change rejected.')
                setRejecting(false)
              },
              failed: (err) => setFormError(asApiErr(err)),
            })
          },
          () => setTried(true),
        )}
      >
        {/* The request being rejected, said again here: the card it sits on is
            behind the dialog. */}
        {pending && (
          <p style={{ margin: 0 }}>
            Change to <strong>{pending.tierLabel}</strong>, asked by {who(pending.requestedBy)}. Reason: {pending.reason}
            {pending.expiresOn ? ' Ends on ' + shortDate(pending.expiresOn) + '.' : ''}
          </p>
        )}
        {formError && <ErrorBox error={formError} what="The rejection" verb="saved" />}
        <div className="field">
          <label htmlFor="tier-reject-note">Reason</label>
          <textarea
            id="tier-reject-note"
            rows={3}
            maxLength={1000}
            value={note}
            aria-invalid={tried && note.trim() === ''}
            onChange={(e) => setNote(e.target.value)}
          />
          {tried && note.trim() === '' && <p className="field-error">Say why, the person who asked reads it</p>}
        </div>
      </Dialog>

      <Dialog
        shape="modal"
        kind="form"
        open={ending}
        onClose={() => setEnding(false)}
        title="End the manual tier"
        {...tierEndFooter({ vendorId: vendor.id, overrideId: standing?.id ?? '', note, busy: tierSave.busy }, (work) => {
          setFormError(null)
          tierSave.send(work, {
            done: () => {
              void refresh("The tier set by hand has ended and the vendor is back on the rules' tier.")
              setEnding(false)
            },
            failed: (err) => setFormError(asApiErr(err)),
          })
        })}
      >
        {formError && <ErrorBox error={formError} what="Ending the manual tier" verb="saved" />}
        <p>
          The vendor goes back to the tier the rules give it{rules ? ', ' + rules.label : ''}.
        </p>
        <div className="field">
          <label htmlFor="tier-end-note">Reason (optional)</label>
          <textarea id="tier-end-note" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Dialog>
    </section>
  )
}
